// ---------------------------------------------------------------------------
// ExampleSections.js
// ---------------------------------------------------------------------------
// Shared demo sections that live on the dedicated /example-website page.
//
// These sections were originally embedded inside the home-page collapsible
// ("Example of a Website"). They were extracted into this file so:
//   • The home page stays lean and CTAs jump visitors to a full demo page.
//   • The demo page can render them stand-alone with its own hero + running
//     dog sprite + paw-print cursor mode.
//
// Nothing in the visual/UX of these sections was changed during extraction —
// same markup, same testids, same data. Only imports moved.
// ---------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award, Calendar, CheckCircle2, ChevronRight,
  ClipboardCheck, GraduationCap, Handshake, Percent, Wallet,
} from 'lucide-react';
import { api } from '../api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../AuthContext';

// ---------------------------------------------------------------------------
// Membership meta — extracted verbatim from Landing.js.
// ---------------------------------------------------------------------------
export const TIER_META = {
  LEVEL1: { tagline: 'Essential maintenance & reinforcement', popular: false },
  LEVEL2: { tagline: 'Comprehensive care & advanced training', popular: true },
  LEVEL3: { tagline: 'Ultimate training & exclusive benefits', popular: false },
};

// ---------------------------------------------------------------------------
// Board & Train compact section
// ---------------------------------------------------------------------------
export const BoardTrainSection = () => {
  const navigate = useNavigate();
  const programs = [
    {
      title: '2 Weeks',
      price: 1900,
      desc: 'Dogs needing basic obedience and manners',
      features: [
        'Foundation obedience training',
        'Basic commands (sit, stay, come, heel)',
        'Leash walking & socialization',
        '2 follow-up sessions with you',
      ],
      popular: false,
    },
    {
      title: '4 Weeks',
      price: 3800,
      desc: 'Dogs with serious behavioral challenges',
      features: [
        'Everything in the 2-week program',
        'Complex behavioral modification',
        'Advanced off-leash reliability',
        'Real-world scenario training',
        '3 follow-up sessions with you',
      ],
      popular: true,
    },
  ];
  const steps = [
    { n: 1, t: 'Initial Consultation', d: "We assess your dog's needs and build a custom plan." },
    { n: 2, t: 'Drop-Off Day', d: "Your dog meets their trainer and settles in at our facility." },
    { n: 3, t: 'Intensive Training', d: "Daily one-on-one sessions with regular video updates to you." },
    { n: 4, t: 'Go-Home Day', d: "Hands-on handover so you can maintain the new skills." },
  ];
  return (
    <section id='board-train' className='py-16 bg-[#FFF8F2] dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='max-w-2xl mx-auto text-center mb-10'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Board & Train</div>
          <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1'>
            Book a Board & Train
          </h2>
          <p className='text-slate-600 dark:text-slate-300 mt-3'>
            Promote your Board & Train Programs and collect lead forms directly on K9os.
          </p>
          <div className='mt-6 flex flex-wrap gap-3 justify-center'>
            <Button
              size='lg'
              className='bg-[#C8102E] hover:bg-[#A50D26] text-white btn-shimmer h-12 px-6'
              onClick={() => navigate('/board-train-form')}
              data-testid='bt-header-apply-cta'
            >
              Apply for Board & Train <ChevronRight className='w-4 h-4 ml-1' />
            </Button>
            <Button
              size='lg'
              variant='outline'
              className='h-12 px-6 border-[#1B3A87]/30 text-[#1B3A87] dark:text-[#93C5FD] dark:border-[#93C5FD]/40 hover:bg-[#1B3A87]/5 dark:hover:bg-[#1B3A87]/10'
              onClick={() => navigate('/book')}
              data-testid='bt-header-book-boarding-cta'
            >
              Book Boarding
            </Button>
          </div>
        </div>

        {/* Value props */}
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10'>
          {[
            { icon: ClipboardCheck, t: 'Customized Plans', d: 'Certified trainers tailor every program to your dog.' },
            { icon: GraduationCap, t: 'Proven Methods', d: 'Thousands of dogs successfully transformed.' },
            { icon: Calendar, t: 'Flexible Programs', d: 'Choose 2 or 4-week immersive programs.' },
            { icon: Award, t: 'Real Results', d: 'Lasting behavioral change with handler training included.' },
          ].map((v, i) => (
            <Card key={i} className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid={`bt-value-${i}`}>
              <div className='w-10 h-10 rounded-lg bg-[#1B3A87]/10 dark:bg-[#1B3A87]/20 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center mb-3'>
                <v.icon className='w-5 h-5' />
              </div>
              <div className='font-display font-bold text-base text-[#0B1220] dark:text-slate-50'>{v.t}</div>
              <p className='text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed'>{v.d}</p>
            </Card>
          ))}
        </div>

        {/* Programs grid */}
        <div className='grid md:grid-cols-2 gap-5 max-w-4xl mx-auto mb-10'>
          {programs.map((p) => (
            <Card
              key={p.title}
              className={`relative p-6 bg-white dark:bg-slate-900 border ${p.popular ? 'border-[#C8102E] shadow-xl md:scale-[1.02]' : 'border-slate-200 dark:border-slate-800 hover:shadow-md'} transition-shadow`}
              data-testid={`bt-program-${p.title.replace(' ', '-').toLowerCase()}`}
            >
              {p.popular && (
                <Badge className='absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8102E] text-white border-0 px-3 py-1 font-bold uppercase tracking-wider text-[10px]'>
                  Most Popular
                </Badge>
              )}
              <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>Board & Train</div>
              <div className='font-display font-bold text-3xl text-[#0B1220] dark:text-slate-50 mt-1'>{p.title}</div>
              <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{p.desc}</div>
              <div className='mt-4 flex items-baseline gap-1'>
                <span className='font-display font-bold text-4xl tabular-nums text-[#0B1220] dark:text-slate-50'>${p.price.toLocaleString()}</span>
              </div>
              <ul className='mt-5 space-y-2'>
                {p.features.map((f) => (
                  <li key={f} className='flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200'>
                    <CheckCircle2 className='w-4 h-4 text-[#167A5A] mt-0.5 shrink-0' /> {f}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Process */}
        <div className='max-w-5xl mx-auto'>
          <div className='text-center mb-6'>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>The Process</div>
            <h3 className='font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50 mt-1'>What to Expect During Board & Train</h3>
          </div>
          <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            {steps.map((s) => (
              <Card key={s.n} className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid={`bt-step-${s.n}`}>
                <div className='w-10 h-10 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-display font-bold mb-3'>{s.n}</div>
                <div className='font-display font-bold text-base text-[#0B1220] dark:text-slate-50'>{s.t}</div>
                <p className='text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed'>{s.d}</p>
              </Card>
            ))}
          </div>
        </div>

        <div className='mt-10 flex flex-wrap gap-3 justify-center'>
          <Button
            size='lg'
            variant='outline'
            className='h-12 px-6 border-[#1B3A87]/30 text-[#1B3A87] dark:text-[#93C5FD] dark:border-[#93C5FD]/40 hover:bg-[#1B3A87]/5 dark:hover:bg-[#1B3A87]/10'
            onClick={() => navigate('/board-train-form#apply')}
            data-testid='landing-board-train-secondary'
          >
            See full program details
          </Button>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Memberships section — pulls tiers from backend and renders 3-tier card grid.
// ---------------------------------------------------------------------------
export const MembershipsPublic = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get('/memberships/tiers').then((r) => setTiers(r.data.tiers)).catch(() => {}); }, []);
  if (tiers.length === 0) return null;
  const goToMembership = () => navigate(user ? '/client/membership' : '/register');
  return (
    <section id='memberships' className='py-16 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='max-w-2xl mx-auto text-center'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-2'>Memberships</div>
          <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50'>The Ultimate Dog Membership</h2>
          <p className='text-slate-600 dark:text-slate-300 mt-3'>Three tiers built for dogs who&apos;ve completed Board & Train. Continue with structured care and ongoing training.</p>
        </div>
        <div className='grid md:grid-cols-3 gap-5 mt-10' data-testid='landing-memberships'>
          {tiers.map((t) => {
            const meta = TIER_META[t.id] || TIER_META.LEVEL1;
            return (
              <Card
                key={t.id}
                className={`relative p-6 border ${meta.popular ? 'border-[#C8102E] shadow-xl md:scale-[1.02]' : 'border-slate-200 dark:border-slate-800 hover:shadow-md'} transition-shadow bg-white dark:bg-slate-900`}
                data-testid={`landing-tier-${t.id}`}
              >
                {meta.popular && (
                  <Badge className='absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8102E] text-white border-0 px-3 py-1 font-bold uppercase tracking-wider text-[10px]'>Most Popular</Badge>
                )}
                <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>{t.label.split('—')[1]?.trim() || 'Tier'}</div>
                <div className='font-display font-bold text-2xl text-[#0B1220] dark:text-slate-50 mt-1'>{t.label.split('—')[0].trim()}</div>
                <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{meta.tagline}</div>
                <div className='mt-5 flex items-baseline gap-1'>
                  <span className='font-display font-bold text-5xl tabular-nums text-[#0B1220] dark:text-slate-50'>${t.price.toFixed(0)}</span>
                  <span className='text-slate-500 dark:text-slate-400'>/month</span>
                </div>
                <ul className='mt-5 space-y-2'>
                  {t.perks.map((p) => (
                    <li key={p} className='flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200'>
                      <CheckCircle2 className='w-4 h-4 text-[#167A5A] mt-0.5 shrink-0' /> {p}
                    </li>
                  ))}
                </ul>
                <Button
                  className='w-full mt-6 h-11 bg-[#1B3A87] hover:bg-[#16306F] text-white'
                  onClick={goToMembership}
                  data-testid={`landing-tier-cta-${t.id}`}
                >
                  Get Started
                </Button>
              </Card>
            );
          })}
        </div>
        <div className='mt-6 text-center text-xs text-slate-500 dark:text-slate-400'>All plans billed monthly via Stripe · cancel anytime</div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Financing section
// ---------------------------------------------------------------------------
const FINANCE_PROVIDERS = [
  {
    name: 'All Pet Card',
    logo: 'https://www.jointforcesk9.com/allpetcard.jpg',
    href: 'https://acquire1.comenity.net/unified/offer-intro?channel=J&clientName=allpetcard&inStore=true&storeNumber=99992&subChannel=S&workflow=unifiedPrequal',
    tagline: 'Pre-qualify with soft check',
  },
  {
    name: 'FlexxBuy',
    logo: 'https://www.jointforcesk9.com/flexxbuy.webp',
    href: 'https://flexxbuy.com/joint-forces-k9-llc/',
    tagline: 'Multiple lenders, one application',
  },
  {
    name: 'LendingUSA',
    logo: 'https://www.jointforcesk9.com/lendingusa.webp',
    href: 'https://portal.lendingusa.com/applications/dtm/application?pid=47614',
    tagline: 'Instant decision financing',
  },
  {
    name: 'PayPal Credit',
    logo: 'https://www.jointforcesk9.com/paypal-logo.svg',
    href: 'https://www.paypal.com/us/webapps/mpp/paypal-credit',
    tagline: '0% interest if paid in 6 months',
  },
];

export const FinancingSection = () => (
  <section id='financing' className='py-16 bg-[#FFF8F2] dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
      <div className='grid lg:grid-cols-3 gap-8 lg:gap-12'>
        {/* Left: Headline & copy */}
        <div className='lg:col-span-1'>
          <Badge className='bg-[#C8102E] text-white border-0 px-3 py-1 font-bold uppercase tracking-wider text-[10px] mb-3'>
            <Percent className='w-3 h-3 mr-1' /> 0% Interest · 6 Months
          </Badge>
          <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50'>
            Dog Training Financing Options
          </h2>
          <p className='text-slate-700 dark:text-slate-300 mt-3 leading-relaxed'>
            We believe life-changing training should be accessible. Flexible financing and
            membership plans help families stay on budget while keeping training on track.
          </p>
          <div className='mt-6 space-y-3'>
            {[
              { icon: Calendar, t: 'Monthly membership tiers for ongoing lessons, boarding nights, and enrichment.' },
              { icon: Wallet, t: 'Payment plans for board-and-train or private lesson packages.' },
              { icon: Handshake, t: 'Bundle discounts when combining boarding, training, and specialty services.' },
            ].map((row, i) => (
              <div key={i} className='flex items-start gap-3'>
                <div className='w-9 h-9 rounded-md bg-[#1B3A87]/10 dark:bg-[#1B3A87]/20 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center shrink-0'>
                  <row.icon className='w-4 h-4' />
                </div>
                <p className='text-sm text-slate-700 dark:text-slate-200 leading-relaxed'>{row.t}</p>
              </div>
            ))}
          </div>
          <p className='text-xs text-slate-500 dark:text-slate-400 mt-6'>
            Talk with our team about the program you want and your budget &mdash; we&apos;ll map out a plan
            that keeps your dog on track without surprises.
          </p>
          <Button
            className='mt-5 bg-[#1B3A87] hover:bg-[#16306F] text-white'
            onClick={(e) => e.preventDefault()}
            data-testid='financing-contact-cta'
          >
            Talk to our team <ChevronRight className='w-4 h-4 ml-1' />
          </Button>
        </div>

        {/* Right: provider cards */}
        <div className='lg:col-span-2'>
          <div className='grid sm:grid-cols-2 gap-4'>
            {FINANCE_PROVIDERS.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target='_blank'
                rel='noopener noreferrer'
                data-testid={`financing-provider-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
                className='group block'
              >
                <Card className='p-5 h-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-[#1B3A87] dark:hover:border-[#93C5FD] transition-all duration-200'>
                  <div className='h-16 flex items-center justify-start mb-4 bg-slate-50 dark:bg-slate-800 rounded-md px-4'>
                    <img
                      src={p.logo}
                      alt={`${p.name} financing logo`}
                      className='max-h-12 max-w-full object-contain'
                      loading='lazy'
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                  <div className='flex items-start justify-between gap-3'>
                    <div>
                      <div className='font-display font-bold text-lg text-[#0B1220] dark:text-slate-50 group-hover:text-[#1B3A87] dark:group-hover:text-[#93C5FD] transition-colors'>
                        {p.name}
                      </div>
                      <p className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{p.tagline}</p>
                    </div>
                    <ChevronRight className='w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform shrink-0 mt-1' />
                  </div>
                </Card>
              </a>
            ))}
          </div>
          <p className='text-xs text-slate-500 dark:text-slate-400 mt-4 text-center sm:text-left'>
            Need help comparing options? Our team is happy to explain the terms so you can choose with confidence.
          </p>
        </div>
      </div>
    </div>
  </section>
);
